"use client";

import React from "react";
import { ArrowRight, Clock, Handshake, MessageSquare, Phone, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const pillars = [{
  id: 1,
  icon: "shield",
  title: "20+ years of frontline experience",
  summary: "Guided by Pete Wood's hands-on site experience, every structure meets rigorous NASC and CHAS standards.",
  tag: "Safety & Compliance"
}, {
  id: 2,
  icon: "message",
  title: "Transparent daily communication",
  summary: "You receive direct site supervisor contact, clear handover certificates and responsive updates.",
  tag: "Clear Communication"
}, {
  id: 3,
  icon: "clock",
  title: "Rock-solid site reliability",
  summary: "We erect, adapt, and dismantle precisely on schedule so your follow-on trades never lose working hours.",
  tag: "Site Reliability"
}, {
  id: 4,
  icon: "handshake",
  title: "Long-term partner relationships",
  summary: "We combine the capacity for tier-one commercial packages with the direct accountability of an established family business.",
  tag: "Long-Term Partnerships"
}];
const stats = [{
  label: "Frontline Leadership",
  value: "20+ Years"
}, {
  label: "Safety Accreditations",
  value: "NASC · CHAS"
}, {
  label: "Coverage",
  value: "South East & London"
}, {
  label: "Core Working Hours",
  value: "Mon-Fri 8am-6pm"
}];
export default function Why() {
  const shouldReduceMotion = useReducedMotion();
  const getPillarIcon = (iconName: string) => {
    switch (iconName) {
      case "shield":
        return <ShieldCheck className="size-6 text-primary" aria-hidden="true" />;
      case "message":
        return <MessageSquare className="size-6 text-primary" aria-hidden="true" />;
      case "clock":
        return <Clock className="size-6 text-primary" aria-hidden="true" />;
      case "handshake":
        return <Handshake className="size-6 text-primary" aria-hidden="true" />;
      default:
        return <ShieldCheck className="size-6 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="why-bmf" data-nav="light" className="relative border-b border-border bg-background py-16 md:py-24">
      <div className="container mx-auto px-5 md:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-md border border-border bg-accent/60 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent-foreground">
            <span className="size-1.5 rounded-full bg-primary" />
            <span>Trusted By Contractors & Homeowners</span>
          </div>

          <h2 className="mt-4 font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Why contractors and homeowners rely on BMF
          </h2>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Scaffolding is the backbone of site safety. We deliver commercial-scale capability with the direct accountability and care of a family-run heritage.
          </p>
        </div>

        {/* 4 Core Pillars Grid */}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((pillar, i) => <motion.div key={pillar.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.45,
          delay: shouldReduceMotion ? 0 : i * 0.075,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex h-full">
              <Card className="relative flex h-full w-full flex-col justify-between overflow-hidden rounded-lg border border-border bg-card p-6 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-md">
                {/* Industrial datum accent bar */}
                <div className="absolute top-0 left-0 h-1 w-full bg-primary" aria-hidden="true" />

                <div>
                  <div className="flex items-center justify-between">
                    <div className="flex size-12 items-center justify-center rounded-md bg-accent">
                      {getPillarIcon(pillar.icon)}
                    </div>
                    <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                      {pillar.tag}
                    </span>
                  </div>

                  <CardHeader className="p-0 pt-6">
                    <CardTitle className="font-default text-lg font-bold text-card-foreground">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-0 pt-3">
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {pillar.summary}
                    </p>
                  </CardContent>
                </div>

                <div className="pt-6">
                  <div className="h-px w-full bg-border" />
                  <div className="pt-3 flex items-center gap-1.5 text-xs font-medium text-foreground">
                    <span className="size-1 rounded-full bg-primary" />
                    Verified Standard
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Operational Parameters Strip */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-12 rounded-lg border border-border bg-card p-6 shadow-xs">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 md:divide-x md:divide-border">
            {stats.map((stat, i) => <div key={stat.label} data-index={i} className="flex flex-col items-center text-center first:pl-0 md:px-4">
                <span className="font-default text-lg font-bold text-foreground sm:text-xl">
                  {stat.value}
                </span>
                <span className="mt-1 text-xs font-medium text-muted-foreground">
                  {stat.label}
                </span>
              </div>)}
          </div>
        </motion.div>

        {/* Action Panel */}
        <div className="mt-12 flex flex-col items-center justify-between gap-6 rounded-lg border border-border bg-accent/40 p-6 sm:flex-row sm:p-8">
          <div>
            <h3 className="font-default text-xl font-bold text-foreground">
              Ready to discuss your site specifications?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Speak directly with our operational team for free quotes and site surveys across the South East.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild size="lg" className="font-default font-semibold">
              <Link to="/ContactRequestQuotePage">
                Request a Quote
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="font-default font-semibold">
              <a href="tel:01344929900">
                <Phone className="size-4" />
                Call Directly
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}